import type { Datum, Series, SeriesTone } from './types';

/**
 * How a series is told apart from its neighbours.
 *
 * Three redundant channels, assigned together and never separately, because
 * this codebase's rule is that colour is never the sole signal — the same rule
 * that makes `StatusBadge` require a tone *and* an icon *and* text.
 *
 *   colour   from the verified categorical ramp
 *   dash     solid, then five patterns
 *   marker   circle, square, diamond, triangle, cross, plus
 *
 * A reader with deuteranopia gets the dash and the marker. A reader printing
 * in greyscale gets the dash and the marker. Nobody is asked to distinguish
 * two lines by hue alone.
 */

/** Six, because a chart needing a seventh series needs a different chart. */
const SERIES_LIMIT = 6;

const TONE_ROLE: Record<SeriesTone, string> = {
  success: 'var(--success-solid)',
  warning: 'var(--warning-solid)',
  critical: 'var(--critical-solid)',
  severe: 'var(--severe-solid)',
  info: 'var(--info-solid)',
  amended: 'var(--amended-solid)',
};

/**
 * The paint for series `index`.
 *
 * A role name, never a colour — which is what lets the whole kit repaint in
 * dark mode, and inside a `.light` subtree nested in `.dark`, without knowing
 * that either exists.
 */
export function seriesColor(index: number, tone?: SeriesTone): string {
  if (tone) return TONE_ROLE[tone];
  return `var(--series-${(index % SERIES_LIMIT) + 1})`;
}

/**
 * Dash pattern for series `index`. Series 1 is solid — the common case should
 * look like a line, not like a legend entry.
 */
const DASHES = ['', '5 3', '1 3', '7 3 2 3', '3 3', '9 3 1 3'] as const;

export function seriesDash(index: number): string | undefined {
  return DASHES[index % SERIES_LIMIT] || undefined;
}

type MarkerShape = 'circle' | 'square' | 'diamond' | 'triangle' | 'cross' | 'plus';

const MARKERS: readonly MarkerShape[] = [
  'circle',
  'square',
  'diamond',
  'triangle',
  'cross',
  'plus',
];

export function seriesMarker(index: number): MarkerShape {
  return MARKERS[index % SERIES_LIMIT]!;
}

/**
 * An SVG path for a marker of size `size`, centred on the origin.
 *
 * Paths rather than `<circle>`/`<rect>` so every marker is one element type and
 * the renderer needs no branch per shape.
 */
export function markerPath(shape: MarkerShape, size = 5): string {
  const r = size / 2;
  switch (shape) {
    case 'circle':
      return `M ${-r} 0 a ${r} ${r} 0 1 0 ${size} 0 a ${r} ${r} 0 1 0 ${-size} 0`;
    case 'square':
      return `M ${-r} ${-r} h ${size} v ${size} h ${-size} Z`;
    case 'diamond':
      return `M 0 ${-r} L ${r} 0 L 0 ${r} L ${-r} 0 Z`;
    case 'triangle':
      return `M 0 ${-r} L ${r} ${r} L ${-r} ${r} Z`;
    case 'cross':
      return `M ${-r} ${-r} L ${r} ${r} M ${-r} ${r} L ${r} ${-r}`;
    case 'plus':
      return `M 0 ${-r} V ${r} M ${-r} 0 H ${r}`;
  }
}

/**
 * Whether a marker is drawn as an outline rather than a fill.
 *
 * Cross and plus have no interior, so filling them paints nothing.
 */
export function markerIsStroke(shape: MarkerShape): boolean {
  return shape === 'cross' || shape === 'plus';
}

/**
 * Hatching for stacked segments beyond the third.
 *
 * Only 4, 5 and 6. Hatching every segment is noise, and the first three are
 * already far enough apart in both hue and lightness to stand alone.
 */
export function seriesHatch(index: number): string | undefined {
  return index >= 3 ? `c-chart-hatch-${(index % SERIES_LIMIT) + 1}` : undefined;
}

/**
 * A one-line description of a series, for the chart's accessible name.
 *
 * Deliberately a summary rather than every number once a series grows past a
 * dozen points. A 60-point, 4-series `aria-label` is 240 numbers in a single
 * uninterruptible string — technically compliant and practically unusable.
 * Beyond that limit the numbers live in the visually-hidden table instead,
 * which a screen reader can navigate cell by cell.
 */
export const SPELL_OUT_LIMIT = 12;

export function describeSeries(series: Series, format: (value: number) => string): string {
  const readings = series.points.filter(
    (point): point is Datum & { value: number } => point.value !== null,
  );
  if (readings.length === 0) return `${series.label}: no data`;

  if (readings.length <= SPELL_OUT_LIMIT) {
    const spelled = readings.map((point) => `${point.label} ${format(point.value)}`).join(', ');
    return `${series.label}: ${spelled}`;
  }

  const values = readings.map((point) => point.value);
  const first = readings[0]!;
  const last = readings[readings.length - 1]!;
  const direction =
    last.value > first.value ? 'rising' : last.value < first.value ? 'falling' : 'level';

  return (
    `${series.label}: ${readings.length} readings from ${first.label} to ${last.label}, ` +
    `${direction}, from ${format(first.value)} to ${format(last.value)}, ` +
    `low ${format(Math.min(...values))}, high ${format(Math.max(...values))}`
  );
}
